// Иконка, цвет и подпись для состояния машины на выдачу.
//
// Одни и те же четыре признака показывают таблица (иконка с тултипом) и мобильная
// карточка (иконка с подписью словами). Пока они описывались отдельно, в таблице
// шестью ветками v-if, а в карточке тернарниками, разъехаться им ничего не мешало.
const STATE_META = {
  1: { icon: "mdi-thumb-up", color: "success", key: "states.normal" },
  2: { icon: "mdi-tools", color: "secondary", key: "states.in_service" },
  3: { icon: "mdi-alert-box", color: "error", key: "states.critical" },
  4: { icon: "mdi-all-inclusive", color: "pink", key: "states.long_rent" },
  5: { icon: "mdi-airplane-off", color: "info", key: "states.no_rent" },
  6: { icon: "mdi-alarm-light", color: "orange", key: "states.service_needed" },
};

// null, а не заглушка: неизвестное состояние лучше не показать вовсе, чем показать
// иконкой, которая означает что-то другое.
export function stateMeta(state) {
  return STATE_META[state] || null;
}

export function stateColorFor(state) {
  const meta = stateMeta(state);
  return meta ? meta.color : "grey";
}

export function stateKeyFor(state) {
  const meta = stateMeta(state);
  return meta ? meta.key : null;
}

export function transmissionMeta(automatic) {
  return automatic
    ? { icon: "mdi-alpha-a-circle", color: "info", key: "transmissions.automatic" }
    : { icon: "mdi-car-shift-pattern", color: "brown", key: "transmissions.manual" };
}

export function cleanMeta(clean) {
  return clean
    ? { icon: "mdi-flare", color: "info", key: "states.clean" }
    : { icon: "mdi-spray-bottle", color: "brown", key: "states.durty" };
}

export function tankMeta(full) {
  return full
    ? { icon: "mdi-battery", color: "light-green", key: "states.gas_full" }
    : { icon: "mdi-battery-10", color: "red", key: "states.gas_not_full" };
}

// ── Тиры состояния (FT-151) ────────────────────────────────────────────────
//
// Шесть состояний плохо различаются цветом: оператору нужно знать не «какое
// именно», а «что делать». Действий три, поэтому цвет кодирует тир срочности,
// а конкретное состояние несёт иконка из STATE_META выше — форма работает и
// без цвета (WCAG 1.4.1), и при дальтонизме.
//
// Тир 0 тоже размечается полоской и подложкой (решение USER 2026-09-09 после
// просмотра на живом гантте): оператору важно видеть, что строка вообще
// размечена, а не гадать, отработал ли рендер. Иконки у него нет — она
// осталась носителем «есть вопросы», и редким остаётся именно она.
const TIERS = {
  0: { name: "in_service_ok", light: "#1B7A3D", dark: "#5BC47F" },
  1: { name: "questions", light: "#B45309", dark: "#FBBF24" },
  2: { name: "unavailable", light: "#B91C1C", dark: "#F87171" },
  3: { name: "long_rent", light: "#BE185D", dark: "#F472B6" },
};

const STATE_TIER = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 2, 6: 1 };

// Состояние вне 1–6. Не «можно выдавать»: валидации state на бэкенде нет, а
// сегодня 7 отображается как «Критическое» — значит неизвестное уже означает
// «не рассчитывать на эту машину». Тир 2 сохраняет эту семантику и оставляет
// поведение fail-safe.
const UNKNOWN_META = {
  icon: "mdi-help-circle",
  color: "grey",
  key: "states.unknown",
};

// Цвет сегмента на диаграмме «Статусы автомобилей». Тир-палитра для неё не
// годится: сегмент на каждое состояние, тиров три, у тира 0 цвета нет вовсе.
// Значения — палитра Окабэ–Ито, различимая при дальтонизме по построению.
const CHART_COLOR = {
  1: "#009E73",
  2: "#7F7F7F",
  3: "#D55E00",
  4: "#CC79A7",
  5: "#0072B2",
  6: "#E69F00",
};
const CHART_COLOR_UNKNOWN = "#3F3F46";

// null/undefined — это дефолт БД (state по схеме default: 1 без null: false),
// и весь продукт трактует его как «Можно выдавать». Числовой мусор — нет.
function isBlank(state) {
  return state === null || state === undefined || state === "";
}

export function stateTierMeta(state) {
  if (isBlank(state)) return { tier: 0, ...TIERS[0], ...STATE_META[1] };
  const known = STATE_META[state];
  if (!known) return { tier: 2, ...TIERS[2], ...UNKNOWN_META };
  const tier = STATE_TIER[state];
  return { tier, ...TIERS[tier], ...known };
}

// Явная запись «неизвестно». Раньше её доставали из stateTierMeta(NaN), что
// держалось на том, что isBlank не считает NaN пустым: любое расширение isBlank
// молча превратило бы «Неизвестно» в «Можно выдавать».
export function unknownTierMeta() {
  return { tier: 2, ...TIERS[2], ...UNKNOWN_META };
}

// Мета вызывается по 8 раз на строку на каждом кадре скролла (виртуальный
// список перерисовывается целиком), а каждый вызов собирал новый объект тремя
// спредами. Результат зависит только от state, поэтому кешируется.
const TIER_CACHE = new Map();
export function stateTierMetaCached(state) {
  const key = state === null || state === undefined ? "∅" : String(state);
  if (!TIER_CACHE.has(key)) TIER_CACHE.set(key, Object.freeze(stateTierMeta(state)));
  return TIER_CACHE.get(key);
}

export function stateTier(state) {
  return stateTierMeta(state).tier;
}

export function stateTierColor(state, isDark) {
  const meta = stateTierMeta(state);
  return isDark ? meta.dark : meta.light;
}

export function stateChartColor(state) {
  if (isBlank(state)) return CHART_COLOR[1];
  return CHART_COLOR[state] || CHART_COLOR_UNKNOWN;
}

// Подпись для диаграммы и легенды: у пустого состояния — подпись состояния 1,
// у мусора — «неизвестно», а не пустая строка, как сейчас в translateCarsStatus.
export function stateLabelKey(state) {
  if (isBlank(state)) return STATE_META[1].key;
  const known = STATE_META[state];
  return known ? known.key : UNKNOWN_META.key;
}

export function tiersLegend() {
  return [0, 1, 2, 3].map((tier) => ({
    tier,
    name: TIERS[tier].name,
    light: TIERS[tier].light,
    dark: TIERS[tier].dark,
    hasIcon: tier !== 0,
    states: Object.keys(STATE_TIER)
      .filter((s) => STATE_TIER[s] === Number(tier))
      .map(Number),
  }));
}
